import { TableTemplateItem, TemplateItem } from "types/";
import {
  LINE_ORIENTATIONS,
  Table,
  TableDividerStyle,
  TableTextStyle,
  TemplateItemWithLayerIndex,
} from "../types";
import {
  extractTextProperties,
  extractTextStyleProperties,
  generateGetPositionFn,
  isLineShape,
  mapServerColor,
  processTemplateAttributes,
} from "./itemsHelper";
import {
  DEFAULT_SUBTITLES_LINE_HEIGHT,
  DEFAULT_TABLE_NAME,
  VIDEO_FPS,
} from "../constants";

/**
 * Extracts shared text style from the first ParameteredText/StreamedText
 * component found in the Table's item_components.
 */
const extractTableTextStyle = (
  component: TemplateItem | undefined,
  getPosition: ReturnType<typeof generateGetPositionFn>
): TableTextStyle | undefined => {
  if (!component) return;

  const { graphics, attributes } = component;
  if (graphics.type !== "ParameteredText" && graphics.type !== "StreamedText") {
    return;
  }

  const { position, width, height, animations } =
    processTemplateAttributes(attributes);
  const { fontAssetId, fontSize, color, strokeColor, lineHeight, textAlign } =
    extractTextProperties(graphics) ?? {};

  const { wrapText, adjustFont, upperCase, verticalAlign } =
    extractTextStyleProperties(graphics);

  if (!width || !position || !fontSize || !fontAssetId || !color) return;

  return {
    fontSize,
    fontAssetId,
    color,
    strokeColor,
    textAlign: textAlign ?? "center",
    lineHeight: lineHeight ?? DEFAULT_SUBTITLES_LINE_HEIGHT,
    wrapText,
    adjustFont,
    upperCase,
    verticalAlign,
    animations,
    animated: graphics.type === "StreamedText",
    position: getPosition(position, null, width, height, "TableText"),
    size: {
      width,
      height,
    },
  };
};

/**
 * Extracts shared divider style from the first Line component found in
 * the Table's item_components.
 */
const extractTableDividerStyle = (
  component: TemplateItem | undefined,
  getPosition: ReturnType<typeof generateGetPositionFn>
): TableDividerStyle | undefined => {
  if (!component) return;

  const { graphics, attributes } = component;
  if (graphics.type !== "Line") return;

  const { position, width, height, animations } =
    processTemplateAttributes(attributes);

  if (!width || !height || !position) return;

  const size = isLineShape(graphics)
    ? {
        width:
          graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? width
            : graphics.thickness,
        height:
          graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? graphics.thickness
            : height,
      }
    : {
        width,
        height,
      };

  return {
    size,
    position: getPosition(
      position,
      null,
      size.width,
      size.height,
      "TableDivider"
    ),
    background: mapServerColor(graphics.color),
    orientation: graphics.orientation,
    animations,
  };
};

/**
 * Maps a Table SpriteDefinition to a UI template Table type.
 * Extracts shared text/divider styles from item_components and
 * table-level position/size from attributes.
 */
export const mapTable = (
  tableItem: TemplateItemWithLayerIndex<TableTemplateItem>,
  getPosition: ReturnType<typeof generateGetPositionFn>
): Table | undefined => {
  const graphics = tableItem.graphics;

  const { position, width, height } = processTemplateAttributes(
    tableItem.attributes
  );

  if (!position || !width || !height) return;

  const tablePosition = getPosition(
    position,
    null,
    width,
    height,
    tableItem.name ?? DEFAULT_TABLE_NAME
  );

  const itemComponents = graphics.item_components as TemplateItem[];

  const textComponent = itemComponents.find(
    (c) =>
      c.graphics.type === "ParameteredText" ||
      c.graphics.type === "StreamedText"
  );
  const lineComponent = itemComponents.find((c) => c.graphics.type === "Line");

  const textStyle = extractTableTextStyle(textComponent, getPosition);
  if (!textStyle) return;

  const extractedDividerStyle = extractTableDividerStyle(
    lineComponent,
    getPosition
  );

  const animationRowDelay =
    graphics.time_interval > 0 ? graphics.time_interval / VIDEO_FPS : undefined;

  return {
    id: tableItem.name ?? DEFAULT_TABLE_NAME,
    position: tablePosition,
    size: { width, height },
    layerIndex: tableItem.layerIndex,
    textStyle,
    showDividers: !!extractedDividerStyle,
    dividerStyle: extractedDividerStyle ?? DEFAULT_DIVIDER_STYLE,
    animationRowDelay,
  };
};

export const DEFAULT_DIVIDER_STYLE: TableDividerStyle = {
  size: { width: 0.6, height: 0.03 },
  position: { x: 0.2, y: 0.97 },
  background: "{brand_kit.palette.secondary_color}",
  orientation: "horizontal",
};
